import axios from 'axios';
import useAuthStore from '@/store/auth';
import Cookies from 'js-cookie';

const baseApiUrl = (process.env.NEXT_PUBLIC_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '')).replace(/\/$/, '');

// Defensive fix: if an environment or cached bundle accidentally points to :9000,
// force it to :8000 and warn in the console so developers can find the root cause.
let resolvedBaseApiUrl = baseApiUrl;
if (resolvedBaseApiUrl.includes(':9000')) {
    console.warn('⚠️ Detected API URL using port 9000 — overriding to port 8000 for safety.');
    resolvedBaseApiUrl = resolvedBaseApiUrl.replace(':9000', ':8000');
}

if (typeof window !== 'undefined') {
    console.log('🔗 API baseURL =', resolvedBaseApiUrl);
}

const api = axios.create({
    baseURL: resolvedBaseApiUrl,
});

// ---------------
// Refresh control
// ---------------
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
    refreshSubscribers.push(cb);
}

function onRefreshed(newToken) {
    refreshSubscribers.forEach((cb) => cb(newToken));
    refreshSubscribers = [];
}

// --------------------------
// Helpers (token + URL clean)
// --------------------------
function extractToken(maybeToken) {
    if (!maybeToken) return '';

    if (typeof maybeToken === 'string') return maybeToken;

    if (typeof maybeToken === 'object' && maybeToken !== null) {
        const t = maybeToken.access_token || maybeToken.token || '';
        if (typeof t === 'string') return t;
        return t ? String(t) : '';
    }

    return String(maybeToken);
}

function removeTokenFromParams(config) {
    if (config?.params && config.params.token) {
        console.warn('⚠️ Token found in params! Removing it for security.');
        delete config.params.token;
    }
}

function removeTokenFromUrl(config) {
    if (!config?.url || !config.url.includes('token=')) return;

    console.warn('⚠️ Token found in URL! Removing it for security.');

    config.url = config.url
        .replace(/[?&]token=[^&]*/g, '') // remove token param(s)
        .replace(/\?&/, '?')
        .replace(/&+/g, '&')
        .replace(/[?&]$/, '')
        .replace(/\?$/, '');
}

function applyToken(config, token) {
    const t = extractToken(token);
    if (!t) return config;

    // safety: make sure token is never passed in URL/params
    removeTokenFromParams(config);
    removeTokenFromUrl(config);

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;

    // Optional debug
    if (typeof window !== 'undefined') {
        const authHeader = config.headers.Authorization;
        if (typeof authHeader !== 'string' || !authHeader.startsWith('Bearer ')) {
            console.error('❌ CRITICAL: Authorization header invalid!', {
                url: config.url,
                authHeader,
                tokenType: typeof t,
            });
        }
    }

    return config;
}

function getTokenWithFallback() {
    // 1) Zustand store
    let token = extractToken(useAuthStore.getState().accessToken);

    if (token) return token;

    if (typeof window === 'undefined') return '';

    // 2) Cookie
    token = extractToken(Cookies.get('access_token'));
    if (token) {
        // best effort hydrate (if you rely on it elsewhere)
        try {
            useAuthStore.getState().hydrate?.();
        } catch (_) {}
        return token;
    }

    // 3) localStorage
    try {
        const ls = localStorage.getItem('access_token');
        token = extractToken(ls);
        if (token) {
            console.log('💾 Using access_token from localStorage in interceptor');
            try {
                useAuthStore.getState().hydrate?.();
            } catch (_) {}
            return token;
        }
    } catch (_) {}

    return '';
}

// -----------------------------
// REQUEST INTERCEPTOR
// -----------------------------
api.interceptors.request.use(
    (config) => {
        // Defensive rewrite: if any code (or cached bundle) attempts to call localhost:9000,
        // rewrite to :8000 so the dev environment talks to the running backend.
        if (typeof config?.url === 'string' && config.url.includes('://localhost:9000')) {
            console.warn('⚠️ Request URL contains :9000 — rewriting to :8000', config.url);
            config.url = config.url.replace(':9000', ':8000');
        }

        // Debug only for /report/process
        if (config.url && config.url.includes('/report/process')) {
            console.log('🔍 DEBUG: Request interceptor - Original URL:', config.url);
            console.log('🔍 DEBUG: Request interceptor - Params:', config.params);
        }

        // Remove token from params/url ASAP
        removeTokenFromParams(config);
        removeTokenFromUrl(config);

        const token = getTokenWithFallback();

        const updatedConfig = applyToken(config, token);

        // Final safety pass
        removeTokenFromParams(updatedConfig);
        removeTokenFromUrl(updatedConfig);

        // Debug only for /report/process
        if (updatedConfig.url && updatedConfig.url.includes('/report/process')) {
            console.log('🔍 DEBUG: Request interceptor - FINAL URL:', updatedConfig.url);
            console.log('🔍 DEBUG: Request interceptor - FINAL Params:', updatedConfig.params);
            console.log(
                '🔍 DEBUG: Has report_id in URL:',
                updatedConfig.url.includes('report_id')
            );
            console.log(
                '🔍 DEBUG: Has report_id in params:',
                !!updatedConfig.params?.report_id
            );

            if (!updatedConfig.url.includes('report_id') && !updatedConfig.params?.report_id) {
                console.error('❌ CRITICAL: report_id is missing from both URL and params!');
                console.error('   URL:', updatedConfig.url);
                console.error('   Params:', updatedConfig.params);
            }
        }

        // Reduced noise auth debug (optional)
        if (typeof window !== 'undefined' && updatedConfig.url && updatedConfig.url.startsWith('/api/')) {
            const hasToken = !!token;
            const hasAuthHeader = !!updatedConfig.headers?.Authorization;

            if (!hasToken) {
                console.error('❌ No token available for request:', updatedConfig.url);
                console.error('   Store token:', useAuthStore.getState().accessToken ? 'exists' : 'missing');
                console.error('   Cookie token:', Cookies.get('access_token') ? 'exists' : 'missing');
                console.error('   All cookies:', Object.keys(Cookies.get()));
            } else if (!hasAuthHeader) {
                console.error('❌ CRITICAL: Token exists but Authorization header is missing!', {
                    url: updatedConfig.url,
                    headersKeys: updatedConfig.headers ? Object.keys(updatedConfig.headers) : [],
                });
            }
        }
        
        // CRITICAL: Логування payload для /pa-tool/report/save/
        if (updatedConfig.url && updatedConfig.url.includes('/pa-tool/report/save/')) {
            console.log('🔍 API Interceptor - Payload being sent:', JSON.stringify(updatedConfig.data, null, 2));
            if (updatedConfig.data) {
                const data = updatedConfig.data;
                if (data.category || data.product) {
                    console.error('❌ CRITICAL: Payload contains singular fields (category/product)!');
                    console.error('   Payload:', data);
                }
                if (data.stage_5_questions && Array.isArray(data.stage_5_questions)) {
                    data.stage_5_questions.forEach((item, index) => {
                        if (!item.product_id) {
                            console.error(`❌ CRITICAL: stage_5_questions[${index}] missing product_id!`, item);
                        }
                        if (!item.questions || !Array.isArray(item.questions)) {
                            console.error(`❌ CRITICAL: stage_5_questions[${index}] missing questions array!`, item);
                        }
                    });
                }
            }
        }

        return updatedConfig;
    },
    (error) => Promise.reject(error)
);

// -----------------------------
// RESPONSE INTERCEPTOR
// -----------------------------
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { refreshAccessToken, logout } = useAuthStore.getState();
        const originalRequest = error.config;

        if (!error.response) {
            return Promise.reject(error);
        }

        const status = error.response.status;

        // Log 401 errors for debugging
        if (status === 401 && typeof window !== 'undefined') {
            const url = originalRequest.url || '';
            const authHeader = originalRequest.headers?.Authorization;
            const tokenPreview = authHeader ? authHeader.substring(0, 30) + '...' : 'none';

            console.error('❌ 401 Unauthorized error:', {
                url,
                method: originalRequest.method,
                hasAuthHeader: !!authHeader,
                tokenPreview,
                responseData: error.response.data,
                responseHeaders: error.response.headers,
            });
        }

        // If not 401 → no refresh needed
        if (status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        const url = originalRequest.url || '';

        // Never refresh on these endpoints
        if (
            url.includes('/auth/refresh') ||
            url.includes('/auth/login') ||
            url.includes('/auth/sign_in') ||
            url.includes('/auth/sign_up')
        ) {
            return Promise.reject(error);
        }

        // Mark retry
        originalRequest._retry = true;

        // If refresh already in progress → wait for it
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                subscribeTokenRefresh((newToken) => {
                    const t = extractToken(newToken);
                    if (!t) {
                        reject(error);
                        return;
                    }
                    const updatedReq = applyToken(originalRequest, t);
                    resolve(api(updatedReq));
                });
            });
        }

        // Begin refresh
        isRefreshing = true;

        try {
            const newToken = extractToken(await refreshAccessToken());
            isRefreshing = false;

            if (!newToken) {
                onRefreshed(null);
                return Promise.reject(error);
            }

            onRefreshed(newToken);

            console.log('🔄 Retrying request with new token:', {
                url: originalRequest.url,
                newTokenPreview: newToken.substring(0, 30) + '...',
            });

            const updatedReq = applyToken(originalRequest, newToken);

            if (!updatedReq.headers?.Authorization) {
                console.error('❌ CRITICAL: Token was not added to retry request!');
                onRefreshed(null);
                return Promise.reject(error);
            }

            return api(updatedReq);
        } catch (refreshError) {
            isRefreshing = false;

            const code = refreshError?.response?.status;

            // Truly invalid refresh token → logout
            if ([400, 401, 403].includes(code)) {
                logout();
            }

            onRefreshed(null);
            return Promise.reject(refreshError);
        }
    }
);

export default api;